import { z } from 'zod/v4';

/**
 * Per-device position settings used by **default-placement** widgets — the
 * data shape produced by the host app's `webWidgetPositionSettingsFormGroup`
 * factory (`apps/websites/src/app/shared/components/web-widget-position-settings/`).
 *
 * Unlike {@link import('./extended-position').extendedPositionSchemaLegacy} this is the
 * "simple" grid: no height, no anchor sides, no `is*PositionChanged` flags —
 * default-placement widgets flow in the document and only carry width/offset/
 * rotation/z-index hints per device.
 *
 * Every field is `nullish()`: the host-app `WidgetPositionLegacy` type marks all
 * fields optional, and persisted widgets created before the position group
 * existed omit it (or parts of it) entirely.
 */
export const widgetPositionSchemaLegacy = z.object({
    width: z.number().nullish(),
    tabletWidth: z.number().nullish(),
    mobileWidth: z.number().nullish(),

    x: z.number().nullish(),
    tabletX: z.number().nullish(),
    mobileX: z.number().nullish(),

    y: z.number().nullish(),
    tabletY: z.number().nullish(),
    mobileY: z.number().nullish(),

    rotate: z.number().nullish(),
    tabletRotate: z.number().nullish(),
    mobileRotate: z.number().nullish(),

    zIndex: z.number().nullish(),
    tabletZIndex: z.number().nullish(),
    mobileZIndex: z.number().nullish(),
});

export type WidgetPositionLegacy = z.infer<typeof widgetPositionSchemaLegacy>;

/**
 * Shape fragment for **default-placement** widget data schemas — `nullish()`
 * because widgets persisted before the position group shipped omit the key.
 * Spread into the data `z.object`. Form twin: `widgetPositionPlugin` in
 * `@sites/entity-signal-form` (`plugins/widget/position.plugin.ts`).
 */
export const widgetPositionPluginShapeLegacy = {
    position: widgetPositionSchemaLegacy.nullish(),
} as const;

/**
 * Default position bag. Mirrors the host-app form factory defaults verbatim —
 * see `webWidgetPositionSettingsFormGroup` for the source of truth.
 */
export const createWidgetPositionLegacy = (overrides: Partial<WidgetPositionLegacy> = {}): WidgetPositionLegacy => ({
    width: 100,
    tabletWidth: 100,
    mobileWidth: 100,
    x: 0,
    tabletX: 0,
    mobileX: 0,
    y: 0,
    tabletY: 0,
    mobileY: 0,
    rotate: 0,
    tabletRotate: 0,
    mobileRotate: 0,
    zIndex: 0,
    tabletZIndex: 0,
    mobileZIndex: 0,
    ...overrides,
});
